import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000/api';
export const API_KEY = process.env.REACT_APP_API_KEY ?? 'better-dog-api-key-2024';

export interface Endereco {
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  complemento?: string;
}

export interface ItemPedidoDTO {
  id: string;
  nome: string;
  quantidade: number;
  precoUnitario: number;
  observacao?: string;
}

export interface CriarPedidoDTO {
  cliente: string;
  telefone?: string;
  endereco: Endereco;
  itens: ItemPedidoDTO[];
  valorItens: number;
  taxaEntrega: number;
  observacao?: string;
  couponCode?: string;
  formaPagamento?: string;
}

const enderecoDefault: Endereco = {
  rua: 'Não informado',
  numero: 'S/N',
  bairro: 'Não informado',
  cidade: 'Não informado',
  estado: '--',
  cep: '00000-000',
};

export const parseEndereco = (enderecoInput: string): Endereco => {
  if (!enderecoInput) {
    return { ...enderecoDefault };
  }

  const enderecoSanitizado = enderecoInput.trim();
  const endereco: Endereco = { ...enderecoDefault };

  try {
    const [ruaNumeroParte, restante] = enderecoSanitizado.split(' - ');

    if (ruaNumeroParte) {
      const [rua, numero] = ruaNumeroParte.split(',').map((parte) => parte.trim());
      if (rua) endereco.rua = rua;
      if (numero) endereco.numero = numero;
    }

    if (restante) {
      const [bairroParte, cidadeEstadoCepParte] = restante.split(',').map((parte) => parte.trim());
      if (bairroParte) endereco.bairro = bairroParte;

      if (cidadeEstadoCepParte) {
        const [cidadeParte, estadoCepParte] = cidadeEstadoCepParte.split('-').map((parte) => parte.trim());
        if (cidadeParte) endereco.cidade = cidadeParte;

        if (estadoCepParte) {
          const [estadoParte, cepParte] = estadoCepParte.split(',').map((parte) => parte.trim());
          if (estadoParte) endereco.estado = estadoParte;
          if (cepParte) endereco.cep = cepParte;
        }
      }
    }
  } catch (error) {
    console.warn('Não foi possível interpretar o endereço informado. Utilizando padrão.', error);
  }

  return endereco;
};

export interface CriarPedidoResponse extends PedidoDTO {
  orderToken?: string;
}

export const criarPedido = async (cnpj: string, payload: CriarPedidoDTO): Promise<CriarPedidoResponse> => {
  const response = await axios.post(`${API_URL}/pedidos`, payload, {
    headers: {
      'Content-Type': 'application/json',
      'x-company-cnpj': cnpj,
      'x-api-key': API_KEY,
    }
  });

  const data = response.data as CriarPedidoResponse;

  if (data.orderToken) {
    try {
      localStorage.setItem('lastOrderToken', data.orderToken);
    } catch {
      // Em ambientes sem acesso a localStorage (SSR/tests), apenas ignorar
    }
  }

  return data;
};

export interface PedidoDTO {
  id: string;
  cliente: string;
  telefone?: string;
  status: string;
  valorItens: number;
  taxaEntrega: number;
  couponDiscount?: number;
  dataCriacao: string;
  dataAtualizacao?: string;
  formaPagamento?: string;
}

export interface PedidoStatusDTO {
  id: string;
  status: string;
  dataCriacao: string;
  dataAtualizacao?: string;
  valorItens: number;
  taxaEntrega: number;
  formaPagamento?: string;
}

export const getLastOrderToken = (): string | null => {
  try {
    return localStorage.getItem('lastOrderToken');
  } catch {
    return null;
  }
};

export const buscarStatusPedidoPorToken = async (token: string): Promise<PedidoStatusDTO> => {
  const response = await axios.get(`${API_URL}/pedidos/status/${encodeURIComponent(token)}`);
  return response.data as PedidoStatusDTO;
};
